import { useSubscription } from '@apollo/client';
import { Spinner, ErrorLocal, PageMessage } from 'components';
import { QuizTypes, SessionTypes } from 'consts';
import { useQuizContext, useQuizSessionContext } from 'contexts';
import { quizSessionProgressSubscription } from 'graphql/subscriptions';
import {
    QuizSessionOverview as QuizSessionOverviewType,
    QuizSessionOverviewVariables,
    QuizSessionOverview_quiz_master_quiz_session_quiz_session_user_progresses as UserSession,
    QuizSessionOverview_quiz_master_quiz_session_quiz_session_user_answers as UserAnswer,
    QuizSessionOverview_quiz_master_quiz_session_session_progress as SessionProgress
} from 'graphql/subscriptions/types/QuizSessionOverview';
import { SlideshowQuickAnswerOverview } from './slideshow-quick-answer-overview';
import { SlideshowDistributedOverview } from './slideshow-distributed-overview';
import { BattleshipOverview } from './battleship-overview';
import { SlideshowPaperlessOverview } from './slideshow-paperless-overview';
import { JeopardyOverview } from '../../jeopardy-quiz';

export interface UserSessionDisplayData {
    id: number;
    userDisplayName: string;
    correctAnswers: number;
    incorrectAnswers: number;
    timeStarted: string;
    timeLastAnswer: string;
    playDuration: string;
    hasEndedSession?: boolean;
}

interface Props {}

/**
 * Component gives the Admin an overview of the session:
 * * the quiz basic info
 * * the setup for the quiz
 * * the players and their scores in real time (using a subscription)
 * @param props
 * @returns
 */
export const QuizSessionOverview = (props: Props) => {
    const sessionContext = useQuizSessionContext();
    const { quiz } = useQuizContext();

    const { data, loading, error } = useSubscription<QuizSessionOverviewType, QuizSessionOverviewVariables>(
        quizSessionProgressSubscription,
        {
            variables: {
                sessionKey: sessionContext!.session.key,
                skipAnswers: quiz.quizTypeId !== QuizTypes.Slideshow || sessionContext?.session.type === SessionTypes.LiveShow
            }
        }
    );

    if (loading) return <Spinner />;
    if (error) return <ErrorLocal errorMessage="Error getting quizzes :(" errorDetails={error} />;

    const userSessions: UserSession[] = data?.quiz_master_quiz_session.at(0)?.quiz_session_user_progresses ?? [];
    const userAnswers: UserAnswer[] =  data?.quiz_master_quiz_session.at(0)?.quiz_session_user_answers ?? [];
    const sessionProgress: SessionProgress | undefined =  data?.quiz_master_quiz_session.at(0)?.session_progress ?? undefined;

    const overviews = {
        [QuizTypes.Slideshow]: {
            [SessionTypes.Distributed]: () => <SlideshowDistributedOverview userSessions={userSessions} />,
            [SessionTypes.LivePaperless]: () => <SlideshowPaperlessOverview userSessions={userSessions} userAnswers={userAnswers} sessionProgress={sessionProgress} />,
            [SessionTypes.LiveQuickAnswer]: () => <SlideshowQuickAnswerOverview userSessions={userSessions} userAnswers={userAnswers} sessionProgress={sessionProgress} />,
            [SessionTypes.LiveShow]: () => <PageMessage message="Invalid combination of quiz and session types!" includeRootLink={true} />
        },
        [QuizTypes.Battleship]: {
            [SessionTypes.Distributed]: () => <BattleshipOverview userSessions={userSessions} />,
            [SessionTypes.LivePaperless]: () => (
                <PageMessage message="Invalid combination of quiz and session types!" includeRootLink={true} />
            ),
            [SessionTypes.LiveQuickAnswer]: () => (
                <PageMessage message="Invalid combination of quiz and session types!" includeRootLink={true} />
            ),
            [SessionTypes.LiveShow]: () => <PageMessage message="Invalid combination of quiz and session types!" includeRootLink={true} />
        },
        [QuizTypes.Sharpshooter]: {
            [SessionTypes.Distributed]: () => <BattleshipOverview userSessions={userSessions} />,
            [SessionTypes.LivePaperless]: () => (
                <PageMessage message="Invalid combination of quiz and session types!" includeRootLink={true} />
            ),
            [SessionTypes.LiveQuickAnswer]: () => (
                <PageMessage message="Invalid combination of quiz and session types!" includeRootLink={true} />
            ),
            [SessionTypes.LiveShow]: () =>
                <PageMessage message="Invalid combination of quiz and session types!" includeRootLink={true} />
        },
        [QuizTypes.Jeopardy]: {
            [SessionTypes.Distributed]: () => <JeopardyOverview />,
            [SessionTypes.LivePaperless]: () => <JeopardyOverview />,
            [SessionTypes.LiveQuickAnswer]: () => <JeopardyOverview />,
            [SessionTypes.LiveShow]: () => <JeopardyOverview />
        }
    };

    return overviews[quiz.quizTypeId as QuizTypes][sessionContext?.session.type as SessionTypes]();
};
